/**
 * Centralized Sentry noise-filtering policy for the user-API layer.
 *
 * Before this, the decision of "what is a real error worth reporting vs.
 * expected/transient noise" was scattered across base.service.ts and absent
 * from Sentry.init(). Keeping the policy here (pure, dependency-free) gives one
 * place to reason about and unit-test it.
 *
 * See SENTRY-ANGULAR-REMEDIATION-PLAN.md — Fixes N1 (login business codes),
 * N2 (global status-0 path) and N3 (manual status-0 capture).
 */

/**
 * Fail-open allowlist of business-error codes that are normal, expected
 * user-facing outcomes (e.g. login flows: a verification code is required, a
 * wrong password) rather than application faults. These are silenced from
 * Sentry. Any unknown or missing code is still reported, so a genuine API
 * failure is never accidentally hidden.
 *
 * Expand only with codes proven to be expected business outcomes.
 */
export const SILENCED_API_ERROR_CODES = new Set<string>([
  'EMAIL_VERIFICATION_CODE_REQUIRED', // confirmed in CD-ANGULAR-6 events
  '2FA_CODE_REQUIRED'
]);

/** Minimal shape of an API response body relevant to noise filtering. */
export interface ApiErrorStatusLike {
  status?: string;
  code?: string;
}

/** Minimal shape of an HttpErrorResponse relevant to noise filtering. */
export interface HttpErrorLike {
  status?: number;
}

/**
 * N1 — should a `{ status: 'error' }` API body be reported to Sentry?
 *
 * Reports iff the body signals an error AND its code is not a known-silenced
 * business code. Fail-open: a missing/unknown code still reports.
 */
export function shouldReportApiErrorStatus(
  res: ApiErrorStatusLike | null | undefined
): boolean {
  if (res?.status !== 'error') {
    return false;
  }
  const code = res.code;
  // `!(code && has)` — NOT `code != null && !has` — so that an absent code
  // still reports (fail-open) instead of being silently swallowed.
  return !(code && SILENCED_API_ERROR_CODES.has(code));
}

/**
 * N3 — should an HttpErrorResponse-like error be reported to Sentry?
 *
 * `status === 0` is a transient network abort / offline / CORS failure (the
 * response never arrived); it is background noise from unstable mobile
 * networks, not an app fault, so it is not reported. Everything else
 * (including 4xx/5xx and 502) is reported. Fail-open on a missing status.
 */
export function shouldReportHttpError(
  err: HttpErrorLike | null | undefined
): boolean {
  return err?.status !== 0;
}

/**
 * N2 — `ignoreErrors` patterns for `Sentry.init()`.
 *
 * When a status-0 HttpErrorResponse reaches Angular's global ErrorHandler it is
 * unwrapped into a string ("Http failure response for <url>: 0 Unknown Error")
 * and the numeric status is no longer accessible — so this path must match on
 * the message text. The `: 0 ` segment is a precise discriminator: real HTTP
 * errors carry `: 4xx`/`: 5xx`. End-anchored as defense-in-depth so the `.+`
 * cannot over-match.
 */
export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
  /Http failure response for .+: 0 Unknown Error$/,
];
